// dropped-entries.ts — CTSK-501 (folder capture). Pure browser-directory logic,
// no component code:
//
//   collectDroppedItems()  SYNCHRONOUS. The DataTransferItemList is only valid
//                          inside the drop event, so webkitGetAsEntry() (and
//                          getAsFile() for items that have no entry) are called
//                          for EVERY item before anything is awaited.
//   readDroppedEntries()   walks the collected entries (async), looping
//                          readEntries() until it returns an empty array
//                          (Chromium returns at most 100 per call), skipping
//                          ignored names, and returns { file, sourceFolder }[].
//   filesFromPicker()      the same result shape from <input webkitdirectory>.
//   summarizeDrop()        classifies a result for the confirmation dialog:
//                          included / ignored / over the cap / unsupported.
//
// sourceFolder is the file's PARENT directory relative to the dropped (or
// picked) root, root folder name included ("Taxes 2025/Receipts"); a file
// directly in the root gets just the root name; a loose file has none. It is
// built from entry names only — never from an absolute path, drive letter or
// user name — and passed through the shared sanitizer. File contents are never
// read during the walk (entry.file() only yields the handle).

import { ACCEPTED_UPLOAD_MIME_TYPES, normalizeSourceFolder } from '@captain-tasko/shared';

export interface DroppedFile {
  file: File;
  /** Relative folder incl. root name, or null for a loose file. */
  sourceFolder: string | null;
}

// Minimal structural views of the File and Directory Entries API, so the walker
// is testable with plain fakes and does not depend on lib.dom's partial types.
export interface EntryLike {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  file?(success: (f: File) => void, failure?: (e: unknown) => void): void;
  createReader?(): { readEntries(success: (entries: EntryLike[]) => void, failure?: (e: unknown) => void): void };
}

export interface CollectedDrop {
  entries: EntryLike[];
  /** Items that had no entry (non-filesystem sources) or a browser without the API. */
  looseFiles: File[];
  hasDirectory: boolean;
}

interface ItemLike {
  kind: string;
  webkitGetAsEntry?: () => unknown;
  getAsFile: () => File | null;
}

/**
 * Collect every dropped item SYNCHRONOUSLY (call this first thing in the drop
 * handler, before any await). `fallbackFiles` is dataTransfer.files, also read
 * synchronously by the caller, used when the item list is empty.
 */
export function collectDroppedItems(
  items: ArrayLike<ItemLike> | null | undefined,
  fallbackFiles?: ArrayLike<File> | null,
): CollectedDrop {
  const entries: EntryLike[] = [];
  const looseFiles: File[] = [];
  const n = items ? items.length : 0;
  for (let i = 0; i < n; i++) {
    const item = items![i]!;
    if (item.kind !== 'file') continue;
    const entry = typeof item.webkitGetAsEntry === 'function' ? (item.webkitGetAsEntry() as EntryLike | null) : null;
    if (entry) {
      entries.push(entry);
    } else {
      const f = item.getAsFile();
      if (f) looseFiles.push(f);
    }
  }
  if (n === 0 && fallbackFiles) for (let i = 0; i < fallbackFiles.length; i++) looseFiles.push(fallbackFiles[i]!);
  return { entries, looseFiles, hasDirectory: entries.some((e) => e.isDirectory) };
}

export interface WalkOptions {
  /** File or folder NAMES to skip (exact, case-sensitive), at any depth. */
  ignore?: readonly string[];
  /** Stop after this many non-ignored files; `truncated` is set. Default 20 000. */
  countLimit?: number;
}

export interface WalkResult {
  files: DroppedFile[];
  /** Distinct ignored entries (a skipped folder counts once, its contents are not walked). */
  ignored: number;
  /** Entries whose file handle could not be obtained. */
  unreadable: number;
  /** The walk stopped at countLimit. */
  truncated: boolean;
  /** Names of the dropped/picked root folders, in order. */
  rootNames: string[];
}

export const DEFAULT_WALK_COUNT_LIMIT = 20_000;

function readAllEntries(dir: EntryLike): Promise<EntryLike[]> {
  return new Promise((resolve, reject) => {
    const reader = dir.createReader?.();
    if (!reader) return resolve([]);
    const all: EntryLike[] = [];
    const next = () => {
      reader.readEntries((batch) => {
        if (batch.length === 0) return resolve(all);
        all.push(...batch);
        next();
      }, reject);
    };
    next();
  });
}

function getFile(entry: EntryLike): Promise<File> {
  return new Promise((resolve, reject) => {
    if (!entry.file) return reject(new Error('not a file entry'));
    entry.file(resolve, reject);
  });
}

function joinPath(parent: string, name: string): string {
  return parent ? `${parent}/${name}` : name;
}

/** Walk already-collected entries. Never reads file contents. */
export async function readDroppedEntries(entries: readonly EntryLike[], opts: WalkOptions = {}): Promise<WalkResult> {
  const ignore = new Set(opts.ignore ?? []);
  const limit = opts.countLimit ?? DEFAULT_WALK_COUNT_LIMIT;
  const result: WalkResult = { files: [], ignored: 0, unreadable: 0, truncated: false, rootNames: [] };

  async function visit(entry: EntryLike, parent: string, isRoot: boolean): Promise<void> {
    if (result.truncated) return;
    if (ignore.has(entry.name)) {
      result.ignored += 1;
      return;
    }
    if (entry.isFile) {
      if (result.files.length >= limit) {
        result.truncated = true;
        return;
      }
      try {
        const file = await getFile(entry);
        result.files.push({ file, sourceFolder: normalizeSourceFolder(parent) });
      } catch {
        result.unreadable += 1;
      }
      return;
    }
    if (entry.isDirectory) {
      const path = joinPath(parent, entry.name);
      if (isRoot) result.rootNames.push(entry.name);
      let children: EntryLike[];
      try {
        children = await readAllEntries(entry);
      } catch {
        result.unreadable += 1;
        return;
      }
      for (const child of children) await visit(child, path, false);
    }
  }

  for (const entry of entries) await visit(entry, '', true);
  return result;
}

/**
 * The same result shape from <input type="file" webkitdirectory>: every file
 * carries webkitRelativePath ("Root/sub/name.ext"). An ignored name anywhere in
 * the path skips the file; all files under one ignored folder count once.
 */
export function filesFromPicker(files: ArrayLike<File>, opts: Pick<WalkOptions, 'ignore'> = {}): WalkResult {
  const ignore = new Set(opts.ignore ?? []);
  const result: WalkResult = { files: [], ignored: 0, unreadable: 0, truncated: false, rootNames: [] };
  const ignoredKeys = new Set<string>();
  const roots = new Set<string>();
  for (let i = 0; i < files.length; i++) {
    const file = files[i]!;
    const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
    const segs = rel.split('/').filter((s) => s !== '');
    const hit = segs.findIndex((s) => ignore.has(s));
    if (hit >= 0) {
      ignoredKeys.add(segs.slice(0, hit + 1).join('/'));
      continue;
    }
    if (segs.length > 1) {
      if (!roots.has(segs[0]!)) {
        roots.add(segs[0]!);
        result.rootNames.push(segs[0]!);
      }
      result.files.push({ file, sourceFolder: normalizeSourceFolder(segs.slice(0, -1).join('/')) });
    } else {
      result.files.push({ file, sourceFolder: null });
    }
  }
  result.ignored = ignoredKeys.size;
  return result;
}

const TEXT_EXT_HINTS = /\.(log|txt|md|yaml|yml|json|xml|csv|tsv|sh|py|js|ts|env|conf|ini|cfg|toml)$/i;

/**
 * The client's view of the server's accepted-type rule: a MIME type on the
 * shared list, or an unclassified one (empty / octet-stream) with a textual
 * extension — which the server accepts after a content sniff. The server stays
 * the authority; a file that passes here can still be rejected there.
 */
export function isSupportedUpload(file: Pick<File, 'name' | 'type'>): boolean {
  if ((ACCEPTED_UPLOAD_MIME_TYPES as readonly string[]).includes(file.type)) return true;
  if (file.type === '' || file.type === 'application/octet-stream') return TEXT_EXT_HINTS.test(file.name);
  return false;
}

export interface FolderDropSummary {
  /** What will be uploaded, in discovery order, at most maxFiles. */
  included: DroppedFile[];
  counts: { included: number; ignored: number; overCap: number; unsupported: number; unreadable: number };
  /** First few unsupported file names, for the end-of-upload report. */
  unsupportedNames: string[];
  truncated: boolean;
  rootNames: string[];
  /** Single-root drops only: does the root folder itself hold files (not only subfolders)? */
  rootHasDirectFiles: boolean;
}

export const UNSUPPORTED_NAMES_SHOWN = 20;

/**
 * Classify a walk/picker result against the org settings. Order: ignored (done
 * by the walk) → unsupported type → cap. Unsupported files are counted, not
 * retried; the cap applies to supported files, in discovery order.
 */
export function summarizeDrop(
  walk: WalkResult,
  settings: { maxFilesPerDrop: number },
  supported: (f: File) => boolean = isSupportedUpload,
): FolderDropSummary {
  const included: DroppedFile[] = [];
  const unsupportedNames: string[] = [];
  let unsupported = 0;
  let overCap = 0;
  for (const df of walk.files) {
    if (!supported(df.file)) {
      unsupported += 1;
      if (unsupportedNames.length < UNSUPPORTED_NAMES_SHOWN) unsupportedNames.push(df.file.name);
    } else if (included.length < settings.maxFilesPerDrop) {
      included.push(df);
    } else {
      overCap += 1;
    }
  }
  const root = walk.rootNames.length === 1 ? walk.rootNames[0]! : null;
  return {
    included,
    counts: { included: included.length, ignored: walk.ignored, overCap, unsupported, unreadable: walk.unreadable },
    unsupportedNames,
    truncated: walk.truncated,
    rootNames: walk.rootNames,
    rootHasDirectFiles: root !== null && included.some((d) => d.sourceFolder === root),
  };
}

/**
 * Defaults for the "Name a group from this folder" row. Only the ROOT folder's
 * name is ever suggested (one group per drop, matching one batch id per
 * drop); with several roots there is nothing sensible to suggest. The box
 * starts ticked only when the root itself holds files — a root that is just a
 * container of subfolders is a weaker group name, so it is opt-in.
 */
export function autoNameDefaults(summary: Pick<FolderDropSummary, 'rootNames' | 'rootHasDirectFiles'>): {
  name: string;
  checkedByDefault: boolean;
  available: boolean;
} {
  if (summary.rootNames.length !== 1) return { name: '', checkedByDefault: false, available: false };
  return { name: summary.rootNames[0]!, checkedByDefault: summary.rootHasDirectFiles, available: true };
}
